import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { AddIcon, LinkIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	ContextMenu,
	Modal,
	Paragraph,
	Popover,
	QuillText,
	Spinner,
} from '@treams/treams-ui';

import LearningCoverHeader from '../components/LearningCoverHeader';
import LearningCourseLinkModal from './LearningCourseLinkModal';

const TypeDot = styled(Box)<{ $color: string }>`
	background: ${(props) => props.$color};
	width: 10px;
	height: 10px;
	border-radius: 5px;
	flex-shrink: 0;
`;

const PROVIDER_TITLES: Record<graphql.LearningCourseProvider, string> = {
	[graphql.LearningCourseProvider.Goodhabitz]: 'GoodHabitz',
};

export default function LearningCourseModal(props: { courseID: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const location = useLocation();
	const [linkToExistingOpen, setLinkToExistingOpen] = useState(false);

	const { data, loading } = graphql.useLearningCourseQuery({
		variables: { id: props.courseID },
	});

	const handleClose = useCallback(() => {
		history.push(location.state?.backgroundLocation ?? '/learning');
	}, [history, location.state?.backgroundLocation]);

	const course = data?.learningCourse;
	const provider = course ? (PROVIDER_TITLES[course.provider] ?? course.provider) : '';
	const description = course?.description ? localize(course.description)?.text : undefined;
	const launchUrl = course
		? (localize(course.launchUrls)?.text ?? course.launchUrl)
		: undefined;

	return (
		<>
			<Popover center visible>
				<Modal>
					<Modal.Header
						title={t('learning.library.course.modal.title')}
						onClose={handleClose}
					/>

					<Modal.Contents p={0}>
						{loading && !course && (
							<Box p={4}>
								<Spinner centered />
							</Box>
						)}

						{course && (
							<>
								<LearningCoverHeader
									imageUrl={course.imageUrl}
									title={localize(course.title)?.text ?? ''}
								/>

								<Box p={4}>
									<Flex alignItems="center" flexWrap="wrap" mb={3}>
										<TypeDot $color={course.learningType.color} mr={2} />
										<Paragraph>
											{[
												localize(course.learningType.title)?.text,
												course.durationMinutes &&
													t('learning.library.course.duration', {
														minutes: course.durationMinutes,
													}),
												course.learningSubjects
													.map((subject) => localize(subject.title)?.text)
													.join(', '),
											]
												.filter(Boolean)
												.join(' · ')}
										</Paragraph>
									</Flex>

									{description && (
										<Box mb={4}>
											<QuillText value={description} />
										</Box>
									)}

									<Flex>
										{launchUrl && (
											<Box mr={2}>
												<Button
													type="primary"
													title={t('learning.library.course.open', {
														provider,
													})}
													onClick={() => {
														window.open(launchUrl, '_blank');
													}}
												/>
											</Box>
										)}
										<ContextMenu.Target
											zIndex={4}
											menu={
												<ContextMenu minWidth={240}>
													<ContextMenu.Item
														icon={<AddIcon />}
														title={t('learning.library.course.linkToGoal.new')}
														onClick={() =>
															history.push(`/goals/new?learning_course_id=${course.id}`)
														}
													/>
													<ContextMenu.Item
														icon={<LinkIcon />}
														title={t('learning.library.course.linkToGoal.existing')}
														onClick={() => setLinkToExistingOpen(true)}
													/>
												</ContextMenu>
											}
										>
											<Button
												type="secondary"
												icon={<LinkIcon />}
												title={t('learning.library.course.linkToGoal')}
											/>
										</ContextMenu.Target>
									</Flex>
								</Box>
							</>
						)}
					</Modal.Contents>
				</Modal>
			</Popover>
			{linkToExistingOpen && course && (
				<LearningCourseLinkModal
					course={course}
					onClose={() => setLinkToExistingOpen(false)}
				/>
			)}
		</>
	);
}
